/**
 * What the reviewer can act on in a draft: the points Boris raised, the paragraph choices, the
 * letter's fields and the task lines. Every control commits a change; the hook re-drafts.
 *
 * Grade paragraphs are shown as choices but can only be swapped for another paragraph of the
 * same template — the wording itself is never edited here (David, Oct 9 2026: no changes to the
 * grading language). The task block is not a choice: its paragraph follows the task list.
 */
import { type ChangeEvent, type KeyboardEvent, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Checkbox } from '@finn-auto/design-system/atoms/checkbox';
import { Input } from '@finn-auto/design-system/atoms/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@finn-auto/design-system/atoms/select';
import { CircleAlert, Plus, X } from 'lucide-react';
import type { DraftBlock, DraftFlag, ReferenceLetterDraft } from '../../types/referenceLetterDraft';
import { cn } from '../../../../shared';

const MAX_TASKS = 10;
const DATE_FIELDS = new Set(['Geburtstag', 'hire_date', 'termination_date', 'document_date']);
const FIELD_LABELS: Record<string, string> = {
  first_name: 'First name',
  last_name: 'Last name',
  Geburtstag: 'Date of birth',
  position: 'Job title',
  department: 'Department',
  hire_date: 'Start date',
  termination_date: 'End date',
  document_date: 'Date of the letter',
  Unterzeichner_Name: 'Signed by',
  Unterzeichner_Position: "Signatory's job title",
};

// An open check: FINN red outline on white. A ticked one: green outline on a desaturated green.
// Red is for what stops the letter; a point waiting for a tick is amber, a ticked one green.
const BLOCKING = 'border-red bg-white';
const TO_TICK = 'border-orange bg-lightOrange';
const DONE = 'border-green bg-green/10';
/** An id for a block's paragraph: block keys carry spaces ("closing phrase - thanks"), an id must not. */
const paragraphId = (key: string) => `paragraph-${key.replace(/[^a-z0-9]+/gi, '-')}`;

// One row shape for every label + control pair, so grades and wording line up.
const ROW = 'grid grid-cols-1 items-center gap-1 sm:grid-cols-[minmax(0,1fr)_16rem] sm:gap-3';

/**
 * The points Boris raised. A tick is the point's `token`: it counts only while the data the point
 * is about is unchanged. A blocking point has no tick — it closes when the field, paragraph or
 * address it names is settled below.
 */
export function FlagList({ flags, onConfirm }: { flags: DraftFlag[]; onConfirm: (token: string, checked: boolean) => void }) {
  if (!flags.length) return <p className="text-sm text-zinc-600">Boris has nothing for you to check on this draft.</p>;
  return (
    <div className="flex flex-col gap-2">
      {flags.filter((f) => f.level === 'blocking').map((f) => (
        <div key={f.code} role="alert" data-testid="flag-blocking" className={cn('flex gap-2 rounded border border-solid p-3 text-sm text-zinc-900', BLOCKING)}>
          <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-red" aria-hidden />
          <span>{f.message}</span>
        </div>
      ))}
      {flags.filter((f) => f.level === 'confirm').map((f) => (
        <div key={f.token} data-testid="flag-confirm" className={cn('rounded border border-solid p-3', f.confirmed ? DONE : TO_TICK)}>
          <Checkbox
            label={f.message}
            labelClassName="text-sm text-zinc-900"
            checked={f.confirmed}
            onCheckedChange={(v) => onConfirm(f.token, v === true)}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * One dropdown per graded block: another paragraph of the same template, never new wording. With
 * `showText`, the chosen paragraph sits under its dropdown as the letter has it (David, Oct 13 2026),
 * so a reviewer reads what a grade says without hunting for it in the letter.
 */
export function BlockChoices({
  blocks, onChoose, showText = false,
}: { blocks: DraftBlock[]; onChoose: (key: string, label: string) => void; showText?: boolean }) {
  return (
    <div className={cn('flex flex-col', showText ? 'gap-4' : 'gap-2')}>
      {blocks.map((b) => {
        const described = showText && b.text ? paragraphId(b.key) : undefined;
        return (
          <div key={b.key} className="flex flex-col gap-2">
            <div className={ROW}>
              <span className="text-sm text-zinc-900">
                {b.name}
                {b.by === 'reviewer' && <span className="ml-2 text-xs text-trustedBlue">changed by you</span>}
              </span>
              <Select value={b.chosen ?? undefined} onValueChange={(v) => onChoose(b.key, v)}>
                <SelectTrigger size="md" aria-label={b.name} aria-describedby={described} className={cn('w-full rounded', !b.chosen && 'border-red')}>
                  <SelectValue placeholder="Choose a paragraph" />
                </SelectTrigger>
                <SelectContent>
                  {b.options.map((o) => (
                    <SelectItem key={o} value={o}>
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {described && (
              <p id={described} data-testid={`paragraph-${b.key}`} className="rounded border-y-0 border-l-4 border-r-0 border-solid border-trustedBlue bg-lightBlue px-3 py-2 text-sm leading-relaxed text-zinc-900">
                {b.text}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Keyed by its value by the caller, so a new value from the proxy starts it afresh. An emptied
 * field goes back to its value rather than being sent: the letter cannot have an empty field.
 */
function FieldInput({ name, value, onCommit }: { name: string; value: string | null; onCommit: (v: string) => void }) {
  const [text, setText] = useState(value ?? '');
  /** Sends a changed, non-empty value; an emptied field goes back to what it was. */
  const commit = () => {
    if (!text.trim()) setText(value ?? '');
    else if (text.trim() !== (value ?? '')) onCommit(text.trim());
  };
  return (
    <Input
      className="rounded"
      label={FIELD_LABELS[name] ?? name}
      type={DATE_FIELDS.has(name) ? 'date' : 'text'}
      value={text}
      onChange={(e: ChangeEvent<HTMLInputElement>) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => e.key === 'Enter' && commit()}
    />
  );
}

/** The letter's fields and the form of address; each commits on its own. */
export function ValueFields({
  draft, onValue, onGender,
}: { draft: ReferenceLetterDraft; onValue: (name: string, v: string) => void; onGender: (g: 'male' | 'female') => void }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <Select value={draft.gender ?? undefined} onValueChange={(v) => onGender(v as 'male' | 'female')}>
        <SelectTrigger label="Address as" className={cn('w-full rounded', !draft.gender && 'border-red')}>
          <SelectValue placeholder="Choose" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="female">{draft.language === 'de' ? 'Frau / sie' : 'Ms. / she'}</SelectItem>
          <SelectItem value="male">{draft.language === 'de' ? 'Herr / er' : 'Mr. / he'}</SelectItem>
        </SelectContent>
      </Select>
      {Object.entries(draft.values).map(([name, value]) => (
        <FieldInput key={`${name}:${value ?? ''}`} name={name} value={value} onCommit={(v) => onValue(name, v)} />
      ))}
    </div>
  );
}

/** Keyed by the task list by the caller, so lines the proxy sends back start it afresh. */
export function TaskLines({ tasks, onChange }: { tasks: string[]; onChange: (tasks: string[]) => void }) {
  const [lines, setLines] = useState(tasks);
  /** Keeps the lines as typed and sends them trimmed, with empty ones dropped. */
  const commit = (next: string[]) => {
    setLines(next);
    onChange(next.map((t) => t.trim()).filter(Boolean));
  };
  return (
    <div className="flex flex-col gap-2">
      {lines.map((t, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="flex-1">
            <Input
              className="rounded"
              label={`Task ${i + 1}`}
              value={t}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setLines(lines.map((x, j) => (j === i ? e.target.value : x)))}
              onBlur={() => commit(lines)}
            />
          </div>
          <Button type="button" variant="ghost" size="sm" className="rounded" aria-label={`Remove task ${i + 1}`} onClick={() => commit(lines.filter((_, j) => j !== i))}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      {lines.length < MAX_TASKS && (
        <div>
          <Button type="button" variant="primary" size="sm" className="rounded" onClick={() => setLines([...lines, ''])}>
            <Plus className="h-4 w-4" /> Add a task
          </Button>
        </div>
      )}
    </div>
  );
}
